import { useState, useCallback, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Хук для управления настройками DIMA
 */
export const useDima = () => {
  const [isEnabled, setIsEnabled] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  /**
   * Получение статуса DIMA
   */
  const fetchStatus = useCallback(async (force = false) => {
    // Если уже был запрос и не указан force, пропускаем
    if (hasFetched.current && !force) {
      return isEnabled;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/dima/enabled`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Не удалось получить статус DIMA';
        try {
          errorMessage = await response.text();
        } catch {
          errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setIsEnabled(data.is_enabled);
      hasFetched.current = true; // Отмечаем, что запрос выполнен
      return data.is_enabled;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  /**
   * Включение/выключение DIMA
   * @param {boolean} isEnable - новый статус
   */
  const setEnabled = useCallback(async (isEnable) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/dima/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_enable: isEnable }),
      });

      if (!response.ok) {
        let errorMessage = 'Не удалось изменить статус DIMA';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `Ошибка ${response.status}`;
        } catch {
          try {
            errorMessage = await response.text();
          } catch {
            errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
          }
        }

        switch (response.status) {
          case 400:
            errorMessage = errorMessage || 'Некорректные данные запроса';
            break;
          case 500:
            errorMessage = errorMessage || 'Внутренняя ошибка сервера';
            break;
          default:
            break;
        }

        throw new Error(errorMessage);
      }

      setIsEnabled(isEnable);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Переключение статуса DIMA
   */
  const toggleEnabled = useCallback(async () => {
    if (isEnabled === null) {
      await fetchStatus(true); // force = true для принудительного запроса
    }
    return await setEnabled(!isEnabled);
  }, [isEnabled, fetchStatus, setEnabled]);

  /**
   * Сброс состояния ошибки
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Сброс флага запроса (для случаев, когда нужно повторно запросить статус)
   */
  const resetFetchFlag = useCallback(() => {
    hasFetched.current = false;
  }, []);

  return {
    isEnabled,
    isLoading,
    error,
    fetchStatus,
    setEnabled,
    toggleEnabled,
    clearError,
    resetFetchFlag,
  };
};